import { EmailSenderForm, NotificationTogglesPanel, SenderDomainPanel } from '@today/ui-domain'
import { useEmailSettings } from '@/hooks/notification-settings/useEmailSettings'
import { useNotificationToggles } from '@/hooks/notification-settings/useNotificationToggles'
import type { TenantConfigSectionProps } from '../section-props'

/**
 * The console's `email` section (TO-382): the sender (provider, from name and
 * address, reply-to, optional own SES credentials), own sending-domain
 * verification with the fallback warning, and the email notification switches.
 * Both planes; the route tree follows the plane, and READ access disables every
 * control.
 */
export function EmailSection({ tenantId, tenantSlug, plane, readOnly }: TenantConfigSectionProps) {
	const email = useEmailSettings(tenantId, tenantSlug, plane)
	const toggles = useNotificationToggles(tenantId, tenantSlug, plane)

	if (email.loadFailed) {
		return (
			<p role="alert" className="text-sm text-ds-danger">
				Could not load the email settings.
			</p>
		)
	}
	if (email.loading) {
		return (
			<p className="text-sm text-ds-text-muted" aria-live="polite">
				Loading the email settings…
			</p>
		)
	}
	return (
		<div className="flex flex-col gap-8">
			<EmailSenderForm
				value={email.draft}
				hasCredentials={email.settings?.binding?.hasCredentials ?? false}
				onChange={email.onChange}
				onSave={email.onSave}
				onRemove={email.onRemove}
				isDisabled={readOnly}
				isBusy={email.isBusy}
				error={email.error}
			/>
			{email.domain ? (
				<SenderDomainPanel
					domain={email.domain}
					record={email.settings?.senderDomain ?? null}
					fallbackAddress={email.fallbackAddress}
					onVerify={email.onVerify}
					onCopy={email.onCopy}
					isDisabled={readOnly}
					isBusy={email.isBusy}
				/>
			) : null}
			<NotificationTogglesPanel
				rows={toggles.rows}
				onToggle={toggles.onToggle}
				onSave={toggles.onSave}
				isDisabled={readOnly}
				isBusy={toggles.isBusy}
			/>
		</div>
	)
}
